"use client";

import * as React from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import Checkbox from "@mui/material/Checkbox";
import Button from "@mui/material/Button";
import FormControlLabel from "@mui/material/FormControlLabel";
import Autocomplete from "@mui/material/Autocomplete";
import { useEffect } from "react";
import labelService from "../services/labelService";

function flattenLabels(labels) {
  let flat = [];
  for (const label of labels) {
    flat.push({
      id: label.id,
      name: label.name,
    });
    if (label.children && label.children.length > 0) {
      flat = flat.concat(flattenLabels(label.children));
    }
  }
  return flat;
}

export function CreateLabelDialog({ open, setOpen, fetchAllData, labelId }) {
  const [name, setName] = React.useState("");
  const [nest, setNest] = React.useState(true);
  const [parent, setParent] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [labels, setLabels] = React.useState([]);
  const [searching, setSearching] = React.useState(false);
  const [isEditMode, setIsEditMode] = React.useState(false);
  const [selectedParent, setSelectedParent] = React.useState(null);

  const typingTimeout = React.useRef(null);

  useEffect(() => {
    if (open) {
      fetchLabels("");
      if (labelId) {
        setIsEditMode(true);
        fetchLabelDetails(labelId);
      } else {
        setIsEditMode(false);
        setName("");
        setParent(null);
        setNest(true);
        setSelectedParent(null);
      }
    }
  }, [open, labelId]);

  async function fetchLabels(search = "") {
    try {
      setSearching(true);
      const res = await labelService.GET_Label_ALL_Search(search);
      const fetchedLabels = res.data?.labels || [];
      const flatLabels = flattenLabels(fetchedLabels);
      setLabels(flatLabels);
    } catch (error) {
      console.error("Error fetching labels:", error);
    } finally {
      setSearching(false);
    }
  }

  async function fetchLabelDetails(id) {
    try {
      const res = await labelService.GET_Label_By_Id(id);
      const labelData = res.data?.data;



      if (labelData) {
        setName(labelData.name || "");

        if (labelData.ParentLabel && labelData.ParentLabel.id) {
          setParent(labelData.ParentLabel.id);
          setSelectedParent({
            id: labelData.ParentLabel.id,
            name: labelData.ParentLabel.name,
          });
          setNest(true);
        } else {
          setParent(null);
          setSelectedParent(null);
          setNest(false);
        }
      }
    } catch (error) {
      console.error("Error fetching label details:", error);
    }
  }

  async function handleSave() {
    if (!name.trim()) return;
    setLoading(true);


    const payload = {
      name,
      color: "#FF0000",
    };


    if (selectedParent && selectedParent.id) {
      payload.parentId = selectedParent.id;
    }


    if (isEditMode && (!selectedParent || !selectedParent.id)) {
      payload.parentId = null;
    }

    try {
      if (isEditMode && labelId) {
        await labelService.Update_Label_By_Id(labelId, payload);
      } else {
        await labelService.CREATE_Label(payload);
      }

      await fetchAllData();
      setOpen(false);
    } catch (error) {
      console.error("Error saving label:", error);
    } finally {
      setLoading(false);
    }
  }


  return (
    <Dialog
      open={open}
      onClose={() => setOpen(false)}
      maxWidth="sm"
      fullWidth

      BackdropProps={{
        style: {
          backgroundColor: "rgba(255,255,255,0.2)",
        
        },
      }}

      PaperProps={{
        style: {
          borderRadius: 16,
          padding: "10px 16px",
          background: "rgba(255,255,255,0.9)",
          backdropFilter: "blur(2px)",
        },
      }}
    >
      <DialogTitle>{isEditMode ? "Edit Label" : "New Label"}</DialogTitle>

      <DialogContent>
        <TextField
          label="Label name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          fullWidth
          margin="normal"
        />

        <FormControlLabel
          control={
            <Checkbox
              checked={nest}
              onChange={(e) => setNest(e.target.checked)}
            />
          }
          label="Nest label under:"
        />

        <Autocomplete
          disabled={!nest}
          options={labels}
          getOptionLabel={(option) => option.name || ""}
          value={
            selectedParent
              ? labels.find((lbl) => lbl.id === selectedParent.id) ||
              selectedParent
              : null
          }
          onChange={(event, newValue) => {
            setParent(newValue ? newValue.id : null);
            setSelectedParent(newValue);
          }}
          onInputChange={(event, value) => {
            if (typingTimeout.current) clearTimeout(typingTimeout.current);
            typingTimeout.current = setTimeout(() => fetchLabels(value), 400);
          }}
          loading={searching}
          renderInput={(params) => (
            <TextField {...params} label="Parent label" margin="normal" />
          )}
        />
      </DialogContent>

      <DialogActions>
        <Button variant="text" onClick={() => setOpen(false)}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={!name.trim() || loading}
        >
          {loading
            ? isEditMode
              ? "Updating..."
              : "Creating..."
            : isEditMode
              ? "Update"
              : "Create"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
